import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1095BVs1095C = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Key Differences Form 1095-B and 1095-C for ACA Reporting"
          description="Learn the difference between Form 1095-B and 1095-C, who must file, deadlines, and how to stay ACA compliant. File your forms easily with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1095-b-vs-1095-c/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          Difference Between{' '}
          <span className="highlight-container">
            <span className="highlight">Form 1095-B</span>
          </span>{' '}
          and{' '}
          <span className="highlight-container">
            <span className="highlight">1095-C</span>
          </span>{' '}
          <br />
          for ACA Reporting
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Understand ACA Reporting Requirements & File the Right Form with
          Confidence
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acareport`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1095-b-vs-1095-c/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1095-B vs 1095-C at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        {'The primary difference is that '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 1095-B reports health coverage received
                        </strong>
                        {', while '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 1095-C reports health coverage offered by large
                          employers
                        </strong>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        {'The form you must file depends on your '}
                        <strong style={{ fontWeight: 600 }}>
                          organization's size, health plan type, and ACA
                          reporting obligations
                        </strong>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Understanding the differences between '}
                        <strong style={{ fontWeight: 600 }}>
                          Forms 1095-B and 1095-C
                        </strong>
                        {
                          ' helps employers choose the correct form, meet IRS reporting requirements, and avoid ACA compliance issues.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                 
                </Stack> */}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                    {' '}
                    ACA Forms
                  </Link>{' '}
                  <span style={{ fontWeight: 600 }}> 1095-B</span> and{' '}
                  <span style={{ fontWeight: 600 }}>1095-C</span> are used to
                  report health coverage information to the IRS and to
                  individuals. Although both forms relate to health insurance
                  coverage, they apply to different entities and serve different
                  compliance purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you're unsure whether your business should{' '}
                  <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                    file Form 1095-B/C
                  </Link>
                  , this guide clearly explains the differences, filing
                  requirements, and reporting obligations.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 1095-B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          What is Form 1095-C?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Differences Between 1095-B and 1095-C
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to stay compliant with TaxZerone?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What is Form 1095-B?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/information-returns/form-1095-b-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1095-B
                  </Link>{' '}
                  is an IRS information return used to report that an individual
                  had a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Minimum Essential Coverage (MEC)
                  </span>{' '}
                  — qualifying health insurance — during the year. It is issued
                  by{' '}
                  <span style={{ fontWeight: 600 }}>
                    health insurance providers, small employers, or government
                    programs
                  </span>{' '}
                  to both the IRS and the individuals covered under the plan.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="form"
                >
                  What is Form 1095-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1095-C
                  </Link>{' '}
                  is an IRS information return used to filed by{' '}
                  <span style={{ fontWeight: 600 }}>
                    Applicable Large Employers (ALEs)
                  </span>{' '}
                  — businesses with 50 or more full-time or full-time equivalent
                  employees. ALEs can file Form 1095-C for all full-time
                  employees, reporting coverage for every month they were
                  employed full-time. TaxZerone ensures accurate filings while
                  staying compliant with IRS requirements.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        File Your ACA Forms Quickly & Accurately with TaxZerone!
                      </Typography>
                      <Typography variant="body1">
                        Ensure your business stays IRS-compliant by filing Forms
                        1095-B and 1095-C with confidence
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acareport`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Differences Between 1095-B and 1095-C
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 16,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Category
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              fontSize: 16,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Form 1095-B
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 16,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Form 1095-C
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Purpose
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Reports that an individual had Minimum Essential
                            Coverage (MEC) during the year.
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Reports whether a large employer offered health
                            coverage to full-time employees and shows employer
                            mandate compliance.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              fontSize: 15,
                              fontWeight: 600,
                            }}
                          >
                            Who files?
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Health insurance providers, government programs
                            (Medicare, Medicaid, CHIP), and small employers with
                            self-insured plans.
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Applicable Large Employers (ALEs) with 50 or more
                            full-time or full-time equivalent employees.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Filing deadlines
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'left',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Recipient Copies:
                            </span>{' '}
                            March 2, 2026;
                            <br />
                            <span style={{ fontWeight: 600 }}>
                              Paper filing:
                            </span>{' '}
                            March 2, 2026 <br />
                            <span style={{ fontWeight: 600 }}>
                              E-filing:
                            </span>{' '}
                            March 31, 2026
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Recipient Copies:
                            </span>{' '}
                            March 2, 2026;
                            <br />
                            <span style={{ fontWeight: 600 }}>
                              Paper filing:
                            </span>{' '}
                            March 2, 2026 <br />
                            <span style={{ fontWeight: 600 }}>
                              E-filing:
                            </span>{' '}
                            March 31, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Information Reported
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'left',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 8 }}>
                                Coverage provider details,{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                covered individuals, and{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                months of coverage.
                              </li>
                            </ul>
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 8 }}>
                                Employer details,{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                Employee details,{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                Plan Start month
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                ALE phone number
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                offer of coverage codes (Line 14),
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                employee premium (Line 15),
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                safe harbor codes (Line 16), and
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                coverage details for self-insured ALEs.
                              </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Who receives the form?
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Individuals enrolled in qualifying health coverage.
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Full-time employees of ALEs.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Transmittal Form
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Filed with Form 1094-B.
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Filed with Form 1094-C (ALE transmittal).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Recipient
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Covered individual (policyholder or responsible
                            individual).
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Full-time employee of the ALE.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              fontSize: 15,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Compliance
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                            }}
                          >
                            Confirms that individuals had qualifying health
                            coverage.
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                              fontSize: 15,
                            }}
                          >
                            Demonstrates compliance with ACA Employer Shared
                            Responsibility provisions.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howto"
                >
                  Common Scenarios
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  1. My company has 30 employees and offers a self-insured
                  health plan. Which form should I file?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your business has fewer than 50 full-time employees and
                  offers a self-insured plan, you should{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1095-B
                  </Link>{' '}
                  to report minimum essential coverage provided to employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  2. Our company has 75 employees and offers health insurance.
                  What form do we file?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your company has 50 or more full-time or full-time
                  equivalent employees, you are an Applicable Large Employer
                  (ALE). You must{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1095-C
                  </Link>{' '}
                  for each full-time employee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  3. We are an ALE with a fully insured plan. Do we file both
                  forms?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. As an ALE, you must{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1095-C
                  </Link>
                  . The insurance provider will{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095b/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1095-B
                  </Link>{' '}
                  to report coverage details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  4. Can a company file both Form 1095-B and Form 1095-C for the
                  same coverage?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  No. A company generally files either{' '}
                  <Link
                    href="/resources/information-returns/form-1095-b-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1095-B
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1095-C
                  </Link>{' '}
                  based on its size and plan type. Filing both for the same
                  coverage may cause duplicate reporting and IRS notices.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, refer to the IRS{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i109495b"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions of Form 1094-B, 1095-B
                        </Link>{' '}
                        and the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form 1094-C, 1095-C
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File the Right ACA Forms?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Understand whether your business must file Form 1095-B or Form
              1095-C.
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              Choosing the right form helps you remain compliant and avoid
              costly IRS penalties.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acareports`}
              >
                Start filing Now
              </Button>
              {/* <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1095BVs1095C;
